import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { dateToHoneycombInput } from 'utils/datetime';

import { VALIDATION_TEXTS_TIME_INPUTS, VALIDATION_TEXTS_DATE_INPUTS, validateYear } from './const';

/**
 * Generic form hook for date/time-based inputs.
 * Handles RHF, date constraints, field-level validation, prefill.
 *
 * @param {object} defaultValues  e.g. { from, fromTime, till, tillTime, reason, note }
 */
export function useFormWithTimeInputs(defaultValues) {
	const today = useMemo(() => new Date(), []);
	const {
		control,
		formState: { dirtyFields, isDirty, isValid, errors },
		getValues,
		setValue,
		reset,
		trigger,
		handleSubmit,
		watch,
	} = useForm({ mode: 'onChange', defaultValues });

	const [miDay, setMinDay] = useState('');

	const defineMinDay = useCallback(() => {
		const { from } = getValues();

		setMinDay(from ? dateToHoneycombInput(from) : '');
	}, [getValues]);

	const isValidDate = useCallback(() => {
		const { from, till } = getValues();

		if (!from || !till) return true;

		if (from > till) return VALIDATION_TEXTS_DATE_INPUTS.endBeforeStartMsg;

		return true;
	}, [getValues]);

	const validateTillTime = useCallback(
		(value) => {
			const { from, fromTime, till } = getValues();

			if (!from || !fromTime || !till || !value) {
				return true;
			}

			const start = new Date(`${from}T${fromTime}`);
			const end = new Date(`${till}T${value}`);

			if (isNaN(start.getTime()) || isNaN(end.getTime())) {
				return true;
			}

			if (start.toISOString() === end.toISOString()) {
				return VALIDATION_TEXTS_TIME_INPUTS.endTimeEqualValidationMsg;
			}

			if (end <= start) {
				return VALIDATION_TEXTS_TIME_INPUTS.endTimeGreaterValidationMsg;
			}

			return true;
		},
		[getValues]
	);

	const handleResetForm = useCallback(() => {
		reset(defaultValues);
		setMinDay('');
	}, [reset, defaultValues]);

	const handleResetSingleField = useCallback((name) => setValue(name, ''), [setValue]);

	return {
		control,
		dirtyFields,
		isDirty,
		isValid,
		errors,
		miDay,
		today,
		defineMinDay,
		isValidDate,
		validateYear,
		validateTillTime,
		getValues,
		setValue,
		trigger,
		reset,
		watch,
		handleResetSingleField,
		handleResetForm,
		handleSubmit,
	};
}
